// src/pages/ArchetypeSelect.tsx
import { useState, useCallback, useMemo, useEffect } from "react";
import { ArchetypeId, ARCHETYPES } from "../constants/archetypes";
import ArchetypeCard from "../components/card/ArchetypeCard";
import { useResponsive } from "../hooks/useResponsive";
import { tiktokPixelService } from "../services/tiktok-pixel.service";

const ARCHETYPE_IDS: ArchetypeId[] = ["heartbeat", "safehaven", "moments"];

// Theme colors for each archetype
const ARCHETYPE_THEMES = {
  moments: {
    primary: "#f9ca24",
    secondary: "#f6e58d",
    accent: "#f0932b",
    glow: "bg-yellow-400/50",
    bg: "bg-yellow-500/[0.05]",
    activeBg: "bg-yellow-600/[0.12]",
    border: "border-yellow-500/40",
    gradient: "from-yellow-500/10",
  },
  heartbeat: {
    primary: "#ff4d6d",
    secondary: "#ff8fa3",
    accent: "#ffb3c6",
    glow: "bg-red-400/50",
    bg: "bg-red-500/[0.05]",
    activeBg: "bg-red-600/[0.12]",
    border: "border-red-500/40",
    gradient: "from-red-500/10",
  },
  safehaven: {
    primary: "#a5b1c2",
    secondary: "#778ca3",
    accent: "#4b6584",
    glow: "bg-blue-400/50",
    bg: "bg-blue-500/[0.05]",
    activeBg: "bg-blue-600/[0.12]",
    border: "border-blue-500/40",
    gradient: "from-blue-500/10",
  },
};

export default function ArchetypeSelect() {
  const [activeIndex, setActiveIndex] = useState(0);
  const { isMobile } = useResponsive();

  const handleSetActive = useCallback((index: number) => {
    setActiveIndex(index);
  }, []);

  const getXOffset = useCallback(
    (index: number): number => {
      if (isMobile) return 0;
      if (activeIndex > index) return index * 20 - 40;
      return index * 20;
    },
    [activeIndex, isMobile]
  );

  // Memoize cards to prevent unnecessary re-renders
  const cards = useMemo(
    () =>
      ARCHETYPE_IDS.map((arch, index) => (
        <ArchetypeCard
          key={arch}
          arch={arch}
          config={ARCHETYPES[arch]}
          theme={ARCHETYPE_THEMES[arch]}
          index={index}
          activeIndex={activeIndex}
          setActive={() => handleSetActive(index)}
          xOffset={getXOffset(index)}
          isMobile={isMobile}
        />
      )),
    [activeIndex, handleSetActive, getXOffset, isMobile]
  );

  useEffect(() => {
    tiktokPixelService.trackViewContent(
      {
        content_id: "archetype_select",
        content_type: "product_group",
        content_name: "LRNSY Archetype Select",
      },
      0,
      "KES",
      "viewcontent:archetype_select"
    );
  }, []);

  return (
    <div className="relative h-screen w-full bg-black text-white overflow-hidden font-sans">
      <div className="relative w-full h-full flex items-center overflow-hidden">{cards}</div>

      <footer className="absolute bottom-0 left-0 w-full z-[100] p-8 flex justify-between items-center opacity-30 pointer-events-none">
        <div className="text-[9px] font-bold uppercase tracking-[0.3em]">LRNSY ©2026</div>
      </footer>
    </div>
  );
}
